import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Wordmark } from "@/components/brand/Logo";
import { SpecialistNote } from "@/components/brand/Specialist";
import { api } from "@/lib/api";
import { flow } from "@/lib/flow";
import { track } from "@/lib/tracking";

const pad = (n) => String(n).padStart(2, "0");

const TIPS = [
  "Ciao, sono Sveta, la tua Beauty Specialist. Rispondi d'istinto: non esistono risposte sbagliate, solo la tua pelle.",
  "Pensa a quando la pelle tira: è il segnale più sincero del suo bisogno di acqua.",
  "Rossori, bruciore o pizzicore mi dicono quanto devo essere delicata nella scelta degli attivi.",
  "Scegli fino a due obiettivi: la prima scelta guiderà la tua routine.",
  "Questa domanda serve solo alla tua sicurezza: alcuni attivi vanno evitati in gravidanza o allattamento.",
  "Terapie, condizioni o trattamenti recenti cambiano ciò che posso consigliarti con serenità.",
  "La tua esperienza con gli attivi mi aiuta a non proporti nulla di troppo forte o troppo blando.",
  "L'età mi aiuta a calibrare prevenzione e trattamenti.",
  "Essenziale o completa? Non c'è una risposta giusta: conta la routine che riuscirai a seguire davvero.",
  "Ultima domanda! Poi ti racconto cosa ho capito della tua pelle.",
];

export default function BeautyTest() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState(flow.answers());
  const [index, setIndex] = useState(flow.index());

  useEffect(() => {
    api.get("/advisor/questions").then((r) => {
      setQuestions(r.data);
      setIndex((i) => Math.min(i, Math.max(r.data.length - 1, 0)));
    });
    if (!flow.started()) { flow.setStarted(); track("test_started"); }
  }, []);

  useEffect(() => { flow.setAnswers(answers); flow.setIndex(index); }, [answers, index]);

  const q = questions[index];
  if (!q) return <div className="min-h-screen bg-[#FAF8F5]" data-testid="beauty-test-loading" />;

  const selected = [].concat(answers[q.id] || []);
  const isLast = index === questions.length - 1;
  const maxChoices = q.multiple ? (q.max_choices || q.options.length) : 1;
  const selectedTags = q.options.filter((o) => selected.includes(o.id)).flatMap((o) => o.tags || []);
  const showText = !!q.free_text_label && (!(q.free_text_when || []).length || q.free_text_when.some((t) => selectedTags.includes(t)));
  const needsContinue = q.multiple || showText;
  const textKey = `${q.id}:text`;

  const next = (ans = answers) => {
    if (isLast) {
      track("test_completed", { questions: questions.length });
      navigate("/analisi");
    } else setIndex(index + 1);
    return ans;
  };

  const choose = (opt) => {
    let value;
    if (q.multiple) {
      value = selected.includes(opt.id) ? selected.filter((id) => id !== opt.id) : [...selected, opt.id].slice(-maxChoices);
    } else value = opt.id;
    const updated = { ...answers, [q.id]: value };
    setAnswers(updated);
    track("question_answered", { question: q.title, answer: opt.label, index: index + 1 });
    const nextTags = q.options.filter((o) => [].concat(value).includes(o.id)).flatMap((o) => o.tags || []);
    const willShowText = !!q.free_text_label && (!(q.free_text_when || []).length || q.free_text_when.some((t) => nextTags.includes(t)));
    if (!q.multiple && !willShowText) setTimeout(() => next(updated), 280);
  };

  const canContinue = selected.length > 0;

  const back = () => (index === 0 ? navigate("/") : setIndex(index - 1));
  const progress = ((index + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex flex-col" data-testid="beauty-test-page">
      <header className="px-6 sm:px-10 pt-5 pb-4 flex items-center justify-between">
        <button data-testid="prev-question-btn" onClick={back} className="fs-btn-ghost">
          <ArrowLeft size={14} strokeWidth={1.5} /> Indietro
        </button>
        <Wordmark height={44} />
        <span data-testid="question-progress-label" className="font-sans text-xs tracking-[0.2em] text-[#595652] tabular-nums w-[68px] text-right">
          {pad(index + 1)}<span className="text-[#C2B8A8]">/{pad(questions.length)}</span>
        </span>
      </header>
      <div className="relative mx-6 sm:mx-10 h-9" data-testid="question-progress">
        <div className="absolute inset-x-0 top-1/2 h-px bg-[#E6DFD5]">
          <motion.div className="h-px bg-[#C5A059]" animate={{ width: `${progress}%` }} transition={{ duration: 0.6, ease: "easeInOut" }} data-testid="question-progress-bar" />
        </div>
        <motion.div className="absolute top-0 -translate-x-1/2 h-9 w-9 rounded-full bg-[#FAF8F5] border border-[#C5A059] flex items-center justify-center shadow-[0_6px_16px_-8px_rgba(26,26,24,.5)]"
          animate={{ left: `${progress}%` }} transition={{ duration: 0.6, ease: "easeInOut" }} data-testid="question-progress-monogram">
          <motion.img key={index} src="/logo-mark.png" alt="" className="h-6 w-6 object-contain"
            initial={{ rotate: -90, scale: 0.6, opacity: 0.4 }} animate={{ rotate: 0, scale: 1, opacity: 1 }} transition={{ duration: 0.6, ease: "easeOut" }} />
        </motion.div>
      </div>
      <main className="flex-1 w-full max-w-xl mx-auto px-6 pt-8 sm:pt-12 pb-16">
        <SpecialistNote text={TIPS[Math.min(index, TIPS.length - 1)]} testid="quiz-specialist-note" className="mb-10" />
        <AnimatePresence mode="wait">
          <motion.div key={q.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }}>
            <p className="fs-eyebrow">Domanda {pad(index + 1)}</p>
            <h1 data-testid="question-title" className="font-serif text-3xl sm:text-4xl leading-[1.15] mt-4">{q.title}</h1>
            {q.subtitle && <p className="text-sm text-[#8C8275] mt-3">{q.subtitle}</p>}
            <div className="mt-10 space-y-3" role="list">
              {q.options.map((opt, i) => {
                const on = selected.includes(opt.id);
                const rankNo = q.multiple && on ? selected.indexOf(opt.id) + 1 : 0;
                return (
                  <button
                    key={opt.id}
                    data-testid={`question-option-${index + 1}-${i}`}
                    aria-pressed={on}
                    onClick={() => choose(opt)}
                    className={`w-full text-left flex items-center justify-between gap-4 px-5 py-4 border rounded-sm transition-colors duration-200 ${
                      on ? "border-[#1A1A18] bg-white" : "border-[#E6DFD5] bg-[#FAF8F5] hover:border-[#C5A059] hover:bg-white"
                    }`}
                  >
                    <span>
                      <span className="block text-[15px] text-[#1A1A18]">{opt.label}</span>
                      {opt.hint && <span className="block text-xs text-[#8C8275] mt-1">{opt.hint}</span>}
                    </span>
                    <span className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-[11px] ${on ? "bg-[#1A1A18] border-[#1A1A18] text-[#FAF8F5]" : "border-[#C2B8A8]"}`} data-testid={rankNo ? `question-option-rank-${rankNo}` : undefined}>
                      {on && (rankNo ? rankNo : <Check size={12} className="text-[#FAF8F5]" />)}
                    </span>
                  </button>
                );
              })}
            </div>
            {q.multiple && (
              <p className="text-xs text-[#8C8275] mt-4" data-testid="question-multi-hint">
                {selected.length === 0 ? `Puoi scegliere fino a ${maxChoices} risposte: la prima è la tua priorità.` : selected.length < maxChoices ? "Vuoi aggiungerne un'altra? Oppure continua." : "Perfetto: la numero 1 è la tua priorità."}
              </p>
            )}
            {showText && (
              <div className="mt-8">
                <label htmlFor="question-free-text" className="block text-xs tracking-[0.18em] uppercase text-[#8C8275]">{q.free_text_label} <span className="normal-case tracking-normal">(facoltativo)</span></label>
                <input id="question-free-text" data-testid="question-free-text-input" value={answers[textKey] || ""} onChange={(e) => setAnswers({ ...answers, [textKey]: e.target.value })}
                  placeholder={q.free_text_when?.length ? "Es. profumo, nichel, oli essenziali…" : "Es. siero alla vitamina C al mattino, retinolo due sere a settimana…"} maxLength={300}
                  className="mt-3 w-full bg-white border border-[#E6DFD5] rounded-sm px-4 py-3 text-sm outline-none focus:border-[#C5A059] placeholder:text-[#8C8275]" />
              </div>
            )}
            {needsContinue && (
              <div className="mt-8 flex items-center justify-end">
                <button data-testid="continue-question-btn" className="fs-btn" disabled={!canContinue} onClick={() => next()}>
                  {isLast ? "Scopri il mio Skin Profile" : "Continua"} <ArrowRight size={14} />
                </button>
              </div>
            )}
            {!q.required && (
              <div className="mt-10 flex items-center justify-end">
                <button data-testid="skip-question-btn" className="fs-btn-ghost" onClick={() => next()}>Salta <ArrowRight size={14} /></button>
              </div>
            )}
            <p className="text-[11px] text-[#8C8275] mt-12 pr-16 sm:pr-0">Le tue risposte vengono salvate automaticamente su questo dispositivo.</p>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
